import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../../context/AppContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    Plus, Package, Users, BarChart3, Search, 
    Edit3, Trash2, ShieldCheck, Image as ImageIcon, 
    X, CheckCircle, RefreshCw 
} from 'lucide-react';
import API from '../../api/axios';

const API_URL = process.env.REACT_APP_API_URL;

const AdminDashboard = ({ closeAdmin }) => {
    const { token, user, logout } = useContext(AppContext);
    const [activeTab, setActiveTab] = useState('inventory');
    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [inventory, setInventory] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    
    // Toggles for New Brand/Category
    const [isNewBrand, setIsNewBrand] = useState(false);
    const [isNewCategory, setIsNewCategory] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        model_name: '', price: '', stock: '', BrandId: '', CategoryId: '',
        short_desc: '', long_desc: '', newBrand: '', newCategory: ''
    });
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [showSuccess, setShowSuccess] = useState(false);

    useEffect(() => {
        if (token) fetchData();
    }, [token, activeTab]);

    const fetchData = async () => {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        try {
            const [b, c, i] = await Promise.all([
            API.get(`${API_URL}/api/admin/brands`, config),
            API.get(`${API_URL}/api/admin/categories`, config),
            API.get(`${API_URL}/api/products`) // Public but used for list
        ]);
            setBrands(b.data);
            setCategories(c.data);
            setInventory(i.data);
        } catch (e) { console.error("Data Fetch Error", e); }
    };

    const handleFileUpload = (e) => setSelectedFiles([...e.target.files]);

    const handleDeploy = async (e) => {
        e.preventDefault();
        const data = new FormData();
        
        // Handle Brand logic (Existing vs New) safely clearing out whitespace or empty payloads
        if (isNewBrand) {
            if (formData.newBrand?.trim()) data.append('newBrandName', formData.newBrand.trim());
        } else {
            if (formData.BrandId) data.append('BrandId', formData.BrandId);
        }

        // Handle Category logic safely clearing out whitespace or empty payloads
        if (isNewCategory) {
            if (formData.newCategory?.trim()) data.append('newCategoryName', formData.newCategory.trim());
        } else {
            if (formData.CategoryId) data.append('CategoryId', formData.CategoryId);
        }

        // Standard fields
        data.append('model_name', formData.model_name?.trim() || '');
        data.append('price', formData.price);
        data.append('stock', formData.stock);
        data.append('short_desc', formData.short_desc?.trim() || '');
        data.append('long_desc', formData.long_desc?.trim() || '');
        
        // Ensure images append properly if attached
        if (selectedFiles && selectedFiles.length > 0) {
            selectedFiles.forEach(file => data.append('images', file));
        }

        try {
            await API.post(`${API_URL}/api/admin/add-stock`, data, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
            });
            setShowSuccess(true);
            setTimeout(() => { setShowSuccess(false); setActiveTab('inventory'); }, 2000);
            
            // Clean up state back to blank configurations
            setFormData({ 
                model_name: '', price: '', stock: '', BrandId: '', CategoryId: '', 
                short_desc: '', long_desc: '', newBrand: '', newCategory: '' 
            });
            setSelectedFiles([]);
        } catch (err) { alert(err.response?.data?.error || "Upload failed"); }
    };

    return (
        <div className="flex h-screen bg-gray-50 text-slate-900 font-sans">
            {/* --- SIDEBAR --- */}
            <aside className="w-72 bg-white border-r border-gray-200 flex flex-col">
                <div className="p-8">
                    <div className="flex items-center gap-3 text-red-600 mb-2">
                        <ShieldCheck size={28} />
                        <span className="font-black text-2xl tracking-tighter text-slate-900">SWIFT<span className="text-red-600">.</span>HQ</span>
                    </div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Internal Admin System</p>
                </div>

                <nav className="flex-1 px-4 space-y-1">
                    <NavBtn icon={<Package size={20}/>} label="Inventory" active={activeTab === 'inventory'} onClick={() => setActiveTab('inventory')} />
                    <NavBtn icon={<Plus size={20}/>} label="Add Product" active={activeTab === 'add'} onClick={() => setActiveTab('add')} />
                    <NavBtn icon={<BarChart3 size={20}/>} label="Statistics" active={activeTab === 'stats'} onClick={() => setActiveTab('stats')} />
                    <NavBtn icon={<Users size={20}/>} label="User Roles" active={activeTab === 'users'} onClick={() => setActiveTab('users')} />
                </nav>

                <div className="p-6 border-t border-gray-100">
                    <div className="flex items-center gap-3 mb-4 px-2">
                        <div className="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center font-bold text-xs">
                            {user?.name?.[0]}
                        </div>
                        <div className="text-xs">
                            <p className="font-bold">{user?.name}</p>
                            <p className="text-gray-400 capitalize">{user?.role}</p>
                        </div>
                    </div>
                    <button onClick={closeAdmin} className="w-full py-3 rounded-xl bg-gray-100 hover:bg-red-50 text-gray-600 hover:text-red-600 font-bold text-sm transition-all">
                        Exit to Store
                    </button>
                </div>
            </aside>

            {/* --- CONTENT --- */}
            <main className="flex-1 overflow-y-auto p-12">
                <AnimatePresence mode="wait">
                    {activeTab === 'add' ? (
                        <motion.div key="add" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                            <h2 className="text-3xl font-black tracking-tight mb-8">Deploy New Stock</h2>
                            
                            <form onSubmit={handleDeploy} className="grid grid-cols-12 gap-8">
                                <div className="col-span-8 space-y-6">
                                    <div className="bg-white p-8 rounded-3xl border border-gray-200 shadow-sm space-y-6">
                                        <div className="grid grid-cols-2 gap-6">
                                            <Input label="Model Name" value={formData.model_name} onChange={v => setFormData({...formData, model_name: v})} placeholder="e.g. ThinkPad X1 Carbon" />
                                            <Input label="Price (KES)" type="number" value={formData.price} onChange={v => setFormData({...formData, price: v})} placeholder="0.00" />
                                        </div>

                                        <div className="grid grid-cols-2 gap-6">
                                            {/* BRAND SECTION */}
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-end">
                                                    <label className="text-xs font-black text-gray-400 uppercase">Brand</label>
                                                    <button type="button" onClick={() => setIsNewBrand(!isNewBrand)} className="text-[10px] font-bold text-red-600 hover:underline">
                                                        {isNewBrand ? "SELECT EXISTING" : "+ ADD NEW BRAND"}
                                                    </button>
                                                </div>
                                                {!isNewBrand ? (
                                                    <select className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:border-red-500 transition-all"
                                                        value={formData.BrandId} onChange={e => setFormData({...formData, BrandId: e.target.value})}>
                                                        <option value="">Select Brand...</option>
                                                        {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                                    </select>
                                                ) : (
                                                    <input className="w-full p-4 bg-red-50 border border-red-200 rounded-2xl outline-none" placeholder="Enter Brand Name"
                                                        value={formData.newBrand} onChange={e => setFormData({...formData, newBrand: e.target.value})} />
                                                )}
                                            </div>

                                            {/* CATEGORY SECTION */}
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-end">
                                                    <label className="text-xs font-black text-gray-400 uppercase">Category</label>
                                                    <button type="button" onClick={() => setIsNewCategory(!isNewCategory)} className="text-[10px] font-bold text-red-600 hover:underline">
                                                        {isNewCategory ? "SELECT EXISTING" : "+ ADD NEW CAT"}
                                                    </button>
                                                </div>
                                                {!isNewCategory ? (
                                                    <select className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:border-red-500 transition-all"
                                                        value={formData.CategoryId} onChange={e => setFormData({...formData, CategoryId: e.target.value})}>
                                                        <option value="">Select Category...</option>
                                                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                                    </select>
                                                ) : (
                                                    <input className="w-full p-4 bg-red-50 border border-red-200 rounded-2xl outline-none" placeholder="Enter Category Name"
                                                        value={formData.newCategory} onChange={e => setFormData({...formData, newCategory: e.target.value})} />
                                                )}
                                            </div>
                                        </div>

                                        <Input label="Short Description" value={formData.short_desc} onChange={v => setFormData({...formData, short_desc: v})} placeholder="Quick highlights..." />
                                        
                                        <div className="space-y-2">
                                            <label className="text-xs font-black text-gray-400 uppercase">Full Product Specifications</label>
                                            <textarea rows="4" className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:border-red-500"
                                                value={formData.long_desc} onChange={e => setFormData({...formData, long_desc: e.target.value})} />
                                        </div>
                                    </div>
                                </div>

                                <div className="col-span-4 space-y-6">
                                    <div className="bg-white p-8 rounded-3xl border border-gray-200 shadow-sm space-y-6">
                                        <Input label="Initial Stock" type="number" value={formData.stock} onChange={v => setFormData({...formData, stock: v})} placeholder="0" />
                                        
                                        <div className="space-y-2">
                                            <label className="text-xs font-black text-gray-400 uppercase">Media Assets</label>
                                            <div className="border-2 border-dashed border-gray-200 rounded-2xl p-6 flex flex-col items-center text-center gap-3 hover:border-red-400 transition-all cursor-pointer relative">
                                                <input type="file" multiple className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleFileUpload} />
                                                <ImageIcon className="text-gray-300" size={32} />
                                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Click to upload images</p>
                                                {selectedFiles.length > 0 && <p className="text-xs text-red-600 font-bold">{selectedFiles.length} files attached</p>}
                                            </div>
                                        </div>

                                        <button type="submit" className="w-full py-5 bg-red-600 text-white rounded-2xl font-black uppercase tracking-widest shadow-xl shadow-red-600/20 hover:bg-red-700 transition-all active:scale-95">
                                            Push to Database
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </motion.div>
                    ) : (
                        <motion.div key="inventory" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                            <div className="flex justify-between items-center mb-10">
                                <h2 className="text-3xl font-black tracking-tight">Active Inventory</h2>
                                <div className="relative">
                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                    <input type="text" placeholder="Search product name, SKU or brand..." className="w-80 pl-12 pr-6 py-3 bg-white border border-gray-200 rounded-full text-sm outline-none focus:ring-2 focus:ring-red-500/20"
                                        value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                                </div>
                            </div>

                            <div className="bg-white rounded-3xl border border-gray-200 overflow-hidden shadow-sm">
                                <table className="w-full text-left">
                                    <thead className="bg-gray-50 border-b border-gray-100">
                                        <tr className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                            <th className="px-8 py-5">Product Details</th>
                                            <th className="px-8 py-5">Category</th>
                                            <th className="px-8 py-5">Price</th>
                                            <th className="px-8 py-5">Stock</th>
                                            <th className="px-8 py-5">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {inventory.filter(p => p.model_name?.toLowerCase().includes(searchQuery.toLowerCase())).map(product => (
                                            <tr key={product.id} className="hover:bg-gray-50/50 transition-colors">
                                                <td className="px-8 py-6">
                                                    <p className="font-bold text-slate-800">{product.model_name}</p>
                                                    <p className="text-[10px] font-mono text-gray-400 uppercase">{product.sku}</p>
                                                </td>
                                                <td className="px-8 py-6">
                                                    <span className="px-3 py-1 bg-gray-100 rounded-full text-[10px] font-black uppercase text-gray-500">{product.Category?.name}</span>
                                                </td>
                                                <td className="px-8 py-6 font-bold text-slate-700">KES {Number(product.price).toLocaleString()}</td>
                                                <td className="px-8 py-6">
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-2 h-2 rounded-full ${product.stock > 5 ? 'bg-green-500' : 'bg-red-500'}`} />
                                                        <span className="font-bold">{product.stock}</span>
                                                    </div>
                                                </td>
                                                <td className="px-8 py-6">
                                                    <div className="flex gap-2">
                                                        <button className="p-2 text-gray-400 hover:text-red-600 transition-colors"><Edit3 size={18}/></button>
                                                        <button className="p-2 text-gray-400 hover:text-red-600 transition-colors"><Trash2 size={18}/></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>

            {/* Success Overlay */}
            <AnimatePresence>
                {showSuccess && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[200] flex items-center justify-center bg-white/80 backdrop-blur-sm">
                        <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="bg-white p-12 rounded-[40px] shadow-2xl border border-gray-100 flex flex-col items-center text-center">
                            <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6">
                                <CheckCircle size={40} />
                            </div>
                            <h3 className="text-2xl font-black mb-2">DEPLOYED SUCCESSFULLY</h3>
                            <p className="text-gray-400">Inventory updated and audit logs synced.</p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const NavBtn = ({ icon, label, active, onClick }) => (
    <button onClick={onClick} className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-bold text-sm transition-all ${active ? 'bg-red-600 text-white shadow-lg shadow-red-600/20' : 'text-gray-400 hover:bg-gray-50 hover:text-slate-900'}`}>
        {icon} {label}
    </button>
);

const Input = ({ label, value, onChange, placeholder, type="text" }) => (
    <div className="space-y-2">
        <label className="text-xs font-black text-gray-400 uppercase">{label}</label>
        <input type={type} placeholder={placeholder} className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-2 focus:ring-red-500/10 focus:border-red-500 transition-all"
            value={value} onChange={e => onChange(e.target.value)} />
    </div>
);

export default AdminDashboard;